import { useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useT } from '@/i18n/use-t';
import { useLocations } from '@/hooks/use-locations';
import {
  useCreateIp2Source,
  useUpdateIp2Source,
  type Ip2Source,
  type Ip2SourceChange,
} from '@/hooks/use-ip2-sources';

/**
 * Add or edit an integration profile 2 source (RL-473).
 *
 * This is what used to be a cleartext settings file on each station. The
 * login entered here is stored encrypted and reaches only the store's Link;
 * the form never shows it back, so editing without typing a new password
 * keeps the stored one.
 *
 * Every catalogue setting is required and none is pre-filled: a store that
 * inherited another store's catalogue code would serve the wrong catalogue
 * without a word, which is the one mistake a default here would invite.
 */

const PREFIX = 'settings.integrations.ip2';

/** `code=unit` pairs, one per line or comma-separated → the unit map. Null when unreadable. */
export function parseUnitMap(raw: string): Record<string, string> | null {
  const map: Record<string, string> = {};
  for (const entry of raw.split(/[\n,]+/)) {
    const trimmed = entry.trim();
    if (trimmed === '') continue;
    const separator = trimmed.indexOf('=');
    if (separator <= 0) return null;
    const code = trimmed.slice(0, separator).trim();
    const unit = trimmed.slice(separator + 1).trim();
    if (code === '' || unit === '') return null;
    map[code] = unit;
  }
  return map;
}

function formatUnitMap(map: Record<string, string>): string {
  return Object.entries(map)
    .map(([code, unit]) => `${code}=${unit}`)
    .join('\n');
}

function serverMessage(cause: unknown, fallback: string): string {
  if (isAxiosError(cause)) {
    const body = cause.response?.data as { message?: unknown } | undefined;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export function Ip2SourceDialog({
  source,
  onClose,
}: {
  /** The source being edited; null to add one. */
  source: Ip2Source | null;
  onClose: () => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const create = useCreateIp2Source();
  const update = useUpdateIp2Source();
  const locations = useLocations({ status: ['active'] });
  const creating = source === null;

  const [locationId, setLocationId] = useState('');
  const [name, setName] = useState(source?.name ?? '');
  const [server, setServer] = useState(source?.settings.server ?? '');
  const [database, setDatabase] = useState(source?.settings.database ?? '');
  const [username, setUsername] = useState(source?.username ?? '');
  const [password, setPassword] = useState('');
  const [catalogCode, setCatalogCode] = useState(source?.settings.hierarchyCatalogCode ?? '');
  const [departmentCode, setDepartmentCode] = useState(source?.settings.departmentCode ?? '');
  const [languageId, setLanguageId] = useState(
    source === null ? '' : String(source.settings.languageId),
  );
  const [currency, setCurrency] = useState(source?.settings.currency ?? '');
  const [unitMap, setUnitMap] = useState(
    source === null ? '' : formatUnitMap(source.settings.unitMap),
  );
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const units = parseUnitMap(unitMap);
    if (units === null) {
      setError(t(`${PREFIX}.form.unitMapInvalid`));
      return;
    }
    const language = Number(languageId);
    if (languageId.trim() === '' || !Number.isSafeInteger(language) || language < 0) {
      setError(t(`${PREFIX}.form.languageIdInvalid`));
      return;
    }
    const settings = {
      server: server.trim(),
      database: database.trim(),
      hierarchyCatalogCode: catalogCode.trim(),
      departmentCode: departmentCode.trim(),
      languageId: language,
      currency: currency.trim().toUpperCase(),
      unitMap: units,
    };
    try {
      if (creating) {
        await create.mutateAsync({
          locationId,
          name: name.trim(),
          username: username.trim(),
          password,
          settings,
        });
      } else {
        const change: Ip2SourceChange = {
          name: name.trim(),
          username: username.trim(),
          enabled,
          settings,
        };
        // Blank means "keep the stored password", not "clear it".
        if (password !== '') change.password = password;
        await update.mutateAsync({ sourceId: source.id, change });
      }
      onClose();
    } catch (cause) {
      setError(serverMessage(cause, t(`${PREFIX}.form.saveError`)));
    }
  }

  const required = [name, server, database, username, catalogCode, departmentCode, currency];
  const canSubmit =
    required.every((value) => value.trim() !== '') &&
    languageId.trim() !== '' &&
    (!creating || (locationId !== '' && password !== ''));

  const field = (
    id: string,
    key: string,
    value: string,
    onChange: (value: string) => void,
    options: { maxLength?: number; inputMode?: 'numeric'; hint?: boolean } = {},
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{t(`${PREFIX}.form.${key}`)}</Label>
      <Input
        id={id}
        value={value}
        maxLength={options.maxLength}
        inputMode={options.inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
      {options.hint === true && (
        <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.${key}Hint`)}</p>
      )}
    </div>
  );

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <AlertDialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {creating ? t(`${PREFIX}.form.createTitle`) : t(`${PREFIX}.form.editTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>{t(`${PREFIX}.form.description`)}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            {creating && (
              <div className="space-y-1.5">
                <Label htmlFor="ip2-location">{t(`${PREFIX}.form.location`)}</Label>
                <Select
                  id="ip2-location"
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  <option value="">{t(`${PREFIX}.form.locationPlaceholder`)}</option>
                  {(locations.data?.items ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {field('ip2-name', 'name', name, setName, { maxLength: 100 })}
            {field('ip2-server', 'server', server, setServer, { maxLength: 255, hint: true })}
            {field('ip2-database', 'database', database, setDatabase, { maxLength: 128 })}
            {field('ip2-username', 'username', username, setUsername, { maxLength: 128 })}
            <div className="space-y-1.5">
              <Label htmlFor="ip2-password">{t(`${PREFIX}.form.password`)}</Label>
              <Input
                id="ip2-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {!creating && (
                <p className="text-xs text-muted-foreground">
                  {t(`${PREFIX}.form.passwordKeepHint`)}
                </p>
              )}
            </div>
            {field('ip2-catalog', 'hierarchyCatalogCode', catalogCode, setCatalogCode, {
              maxLength: 64,
              hint: true,
            })}
            {field('ip2-department', 'departmentCode', departmentCode, setDepartmentCode, {
              maxLength: 64,
            })}
            {field('ip2-language', 'languageId', languageId, setLanguageId, {
              inputMode: 'numeric',
            })}
            {field('ip2-currency', 'currency', currency, setCurrency, { maxLength: 3, hint: true })}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ip2-unit-map">{t(`${PREFIX}.form.unitMap`)}</Label>
              <Textarea
                id="ip2-unit-map"
                value={unitMap}
                rows={3}
                placeholder={'1=each\n5=kg'}
                onChange={(event) => setUnitMap(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.unitMapHint`)}</p>
            </div>
            {!creating && (
              <div className="flex items-end gap-2">
                <input
                  id="ip2-enabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
                <Label htmlFor="ip2-enabled">{t(`${PREFIX}.form.enabled`)}</Label>
              </div>
            )}
          </div>

          {error !== null && <p className="text-sm text-destructive">{error}</p>}

          <AlertDialogFooter>
            <AlertDialogCancel type="button" onClick={onClose}>
              {t('settings.integrations.cancel')}
            </AlertDialogCancel>
            <Button type="submit" disabled={pending || !canSubmit}>
              {creating ? t(`${PREFIX}.form.createSubmit`) : t(`${PREFIX}.form.editSubmit`)}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
